export interface UpdateContentEntry {
  /** contentType from URL route — e.g. "business", "career", "navigation" */
  page: string;
  /** contentUuid of the saved draft (from new-draft API response) */
  contentUuid: string;
  /** contentVersionUuid of the saved draft (from new-draft API response) */
  contentVersionUuid: string;
  /** sectionId from URL route — e.g. "bussinessContactSection", "heroSection" */
  section: string;
}

export type UpdateContentStore = Record<string, UpdateContentEntry>;

// ── Static storage keys ───────────────────────────────────────────────────────
// These are the fixed keys used regardless of sectionId or apiSlug.

export const CONTENT_STORE_KEYS = {
  HEADER: 'header',
  FOOTER: 'footer',
  PRODUCT: 'product',
  CONTACT: 'contact',
  SALE: 'sale',
  BUSINESS: 'business',
  JOB_DETAIL: 'job-detail',
  COMPANY: 'company',
  CAREER: 'career',
} as const;

export type ContentStoreKey = typeof CONTENT_STORE_KEYS[keyof typeof CONTENT_STORE_KEYS];

// ── localStorage key ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'update-content';

// ── Read ──────────────────────────────────────────────────────────────────────

/** Returns the full store, or {} if empty / parse error. */
export function getUpdateContentStore(): UpdateContentStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UpdateContentStore;
  } catch {
    return {};
  }
}

/** Returns the entry for a specific static key, or undefined if not found. */
export function getUpdateContentEntry(
  key: ContentStoreKey,
): UpdateContentEntry | undefined {
  return getUpdateContentStore()[key];
}

export function saveUpdateContent(
  storeKey: ContentStoreKey | string, //NOSONAR
  page: string,
  contentUuid: string,
  contentVersionUuid: string,
  section: string,
): void {
  try {
    const store = getUpdateContentStore();
    store[storeKey] = { page, contentUuid, contentVersionUuid, section };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[updateContentStorage] write failed:', err);
  }
}

/** Removes a single entry by static key. */
export function removeUpdateContentEntry(key: ContentStoreKey): void {
  try {
    const store = getUpdateContentStore();
    delete store[key];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[updateContentStorage] remove failed:', err);
  }
}

/** Clears the entire store. */
export function clearUpdateContentStore(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('[updateContentStorage] clear failed:', err);
  }
}